import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { ArrowLeft, MapPin, Phone, Calendar, User, MessageSquare, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const bureauColors = {
  Experian: '#1e40af',
  Equifax: '#059669',
  TransUnion: '#d97706'
};

export default function PartnerClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [scores, setScores] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, scoresRes, notesRes] = await Promise.all([
          axios.get(`${API_URL}/api/clients/${clientId}`),
          axios.get(`${API_URL}/api/credit-scores/${clientId}`),
          axios.get(`${API_URL}/api/notes/${clientId}`).catch(() => ({ data: [] }))
        ]);
        setClient(clientRes.data);
        setScores(scoresRes.data);
        setNotes(notesRes.data);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  const chartData = scores.map((s) => ({
    date: s.recorded_date ? format(new Date(s.recorded_date), 'MMM d') :
          s.updated_at ? format(new Date(s.updated_at), 'MMM d') : 'N/A',
    Experian: s.experian,
    Equifax: s.equifax,
    TransUnion: s.transunion
  }));

  const latestScore = scores.length > 0 ? scores[scores.length - 1] : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a2e5a]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Client not found</p>
          <Button variant="outline" onClick={() => navigate('/partner')} className="mt-4">
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-client-detail">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/partner')}
          data-testid="back-to-list"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>

        {/* Client Header */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="client-info-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-[#1a2e5a]/10 flex items-center justify-center">
                <span className="text-xl font-semibold text-[#1a2e5a]">
                  {client.first_name?.[0]}{client.last_name?.[0]}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{client.first_name} {client.last_name}</h2>
                <Badge className={
                  client.status === 'Active Client' || client.status === 'active'
                    ? 'bg-emerald-100 text-emerald-800 text-xs mt-1'
                    : client.status === 'Collection Attention'
                    ? 'bg-amber-100 text-amber-800 text-xs mt-1'
                    : 'bg-slate-100 text-slate-800 text-xs mt-1'
                }>
                  {client.status}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Full Name</p>
                  <p className="text-sm text-slate-900">{client.first_name} {client.last_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Address</p>
                  <p className="text-sm text-slate-900">{client.address || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Date of Birth</p>
                  <p className="text-sm text-slate-900">
                    {client.dob ? format(new Date(client.dob + 'T00:00:00'), 'MMM d, yyyy') : 'Not provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Phone</p>
                  <p className="text-sm text-slate-900">{client.phone || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credit Score Chart */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="score-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900">Credit Score Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis domain={[300, 850]} stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="Experian" stroke={bureauColors.Experian} strokeWidth={2.5} dot={{ fill: bureauColors.Experian, r: 4 }} />
                    <Line type="monotone" dataKey="Equifax" stroke={bureauColors.Equifax} strokeWidth={2.5} dot={{ fill: bureauColors.Equifax, r: 4 }} />
                    <Line type="monotone" dataKey="TransUnion" stroke={bureauColors.TransUnion} strokeWidth={2.5} dot={{ fill: bureauColors.TransUnion, r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No score data available for this client
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 3 Individual Bureau Score Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {['Experian', 'Equifax', 'TransUnion'].map((bureau) => (
            <Card key={bureau} className="bg-white border-slate-200 shadow-sm" data-testid={`score-${bureau.toLowerCase()}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{bureau}</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: bureauColors[bureau] }}>
                      {latestScore ? latestScore[bureau.toLowerCase()] || '---' : '---'}
                    </p>
                  </div>
                  <div className="w-3 h-10 rounded-full" style={{ backgroundColor: bureauColors[bureau] }} />
                </div>
                <div className="h-20">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis dataKey="date" tick={false} axisLine={false} />
                        <YAxis domain={[300, 850]} hide />
                        <Line
                          type="monotone"
                          dataKey={bureau}
                          stroke={bureauColors[bureau]}
                          strokeWidth={2.5}
                          dot={{ fill: bureauColors[bureau], r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-slate-400">No data</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Score History Table */}
        {scores.length > 0 && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="score-history-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900">Score History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">Experian</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">Equifax</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">TransUnion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...scores].reverse().map((score, index) => (
                    <TableRow key={index} className="hover:bg-slate-50">
                      <TableCell className="text-slate-900">
                        {score.recorded_date ? format(new Date(score.recorded_date), 'MMM d, yyyy') :
                         score.updated_at ? format(new Date(score.updated_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold" style={{ color: bureauColors.Experian }}>{score.experian || '-'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold" style={{ color: bureauColors.Equifax }}>{score.equifax || '-'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold" style={{ color: bureauColors.TransUnion }}>{score.transunion || '-'}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Notes / Activity */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="notes-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-slate-400" />
              <CardTitle className="text-lg font-semibold text-slate-900">Notes & Activity</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {notes.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No activity recorded yet</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {notes.map((note, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-[#1a2e5a]/10 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-3.5 h-3.5 text-[#1a2e5a]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-700">{note.created_by_name || 'Staff'}</p>
                        <p className="text-xs text-slate-400">
                          {note.created_at ? format(new Date(note.created_at), 'MMM d, yyyy') : ''}
                        </p>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{note.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
